import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { RECEIPT_CONFIGURE_SLICE_NAME as SLICE_NAME } from "./configure.constants"

/* ------------- Initial State ------------- */

// Configure wizard state (§11 in CHA1-1769-related docs). The whole thing is
// server-owned: on every mount the page calls externalOps.loadProfile() and then
// hydrateConfigure() replaces this slice wholesale — nothing here is persisted
// across sessions. saveSection is local-only (marks a section dirty); the actual
// PATCH/POST goes through externalOps.saveDraft/submitProfile from the components.

const initialState = {
  profile: {
    // Wizard answers, keyed by step (business_info / business_details / ...).
    buildingBlocks: {},
    // Server's own wizard block (completed_steps + mode flags). Single source of
    // truth for "is this step Done" and "is setup finished".
    wizard: null,
    // Business account this state was hydrated for. Null = "not hydrated yet":
    // consumers must not save, and must not trust what they are rendering.
    hydratedBaId: null,
    // Live version a Profile update is written against (§11.13). Server-owned.
    baseVersionId: null,
    // Serializer's `updated` field, shown as "Updated <date>" on the profile card.
    lastUpdated: null,
    // Set true after a Profile update is submitted, until the mock backend applies.
    hasPendingChanges: false
  },
  ui: {
    // idle | saving | saved | error — drives the footer save-status pill.
    autoSaveStatus: "idle",
    // { [sectionKey]: true } for every section with unsaved local edits.
    dirtySections: {}
  }
}

/* ------------- Slice ------------- */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    // Replace every server-owned piece of Configure state in one step, from one
    // Configure GET. A replace (not a merge): an empty response is a real answer,
    // and merging would let stale local answers survive it.
    hydrateConfigure(state, { payload }) {
      state.profile.buildingBlocks = payload?.blocks ?? {}
      state.profile.wizard = payload?.wizard ?? null
      state.profile.hydratedBaId = payload?.baId ?? null
      state.profile.baseVersionId = payload?.baseVersionId ?? null
      state.profile.lastUpdated = payload?.lastUpdated ?? state.profile.lastUpdated ?? null
    },
    // Cleared when the account context changes, so nothing renders the previous
    // account's answers while the new request is still in flight.
    resetConfigure(state) {
      state.profile.buildingBlocks = {}
      state.profile.wizard = null
      state.profile.hydratedBaId = null
      state.profile.baseVersionId = null
      state.ui.dirtySections = {}
    },
    // The wizard block alone, from a save's own response. Only this block: the
    // reply echoes committed steps, so replacing buildingBlocks would drop the
    // edits the merchant is still making on the step they are standing on.
    setWizard(state, { payload }) {
      state.profile.wizard = payload?.wizard ?? state.profile.wizard
    },
    // Local-only field edit — merges into the section's buildingBlocks and marks
    // it dirty. The real PATCH happens in the Footer via externalOps.saveDraft.
    saveSection(state, { payload }) {
      const section = payload?.section
      if (!section) return
      const patch = payload?.patch ?? {}
      const prev = state.profile.buildingBlocks?.[section] ?? {}
      state.profile.buildingBlocks[section] = { ...prev, ...patch }
      state.ui.dirtySections[section] = true
    },
    // Clear the dirty flag once a section has been committed to the server.
    clearSectionDirty(state, { payload }) {
      const section = payload?.section
      if (!section) return
      delete state.ui.dirtySections[section]
    },
    // After a profile edit is submitted, drives the amber "changes under review"
    // banner until the backend applies them.
    setPendingChanges(state, { payload }) {
      state.profile.hasPendingChanges = !!payload
    },
    // Footer autosave status pill.
    setAutoSaveStatus(state, { payload }) {
      state.ui.autoSaveStatus = payload || "idle"
    }
  }
})

export const {
  hydrateConfigure,
  resetConfigure,
  setWizard,
  saveSection,
  clearSectionDirty,
  setPendingChanges,
  setAutoSaveStatus
} = slice.actions

/* ------------- Selectors ------------- */

export const selectBuildingBlocks = (state) => state.receiptConfigure.profile.buildingBlocks
export const selectWizard = (state) => state.receiptConfigure.profile.wizard
export const selectIsHydrated = (state) => !!state.receiptConfigure.profile.hydratedBaId
export const selectBaseVersionId = (state) => state.receiptConfigure.profile.baseVersionId
export const selectLastUpdated = (state) => state.receiptConfigure.profile.lastUpdated
export const selectHasPendingChanges = (state) => state.receiptConfigure.profile.hasPendingChanges
export const selectDirtySections = (state) => state.receiptConfigure.ui.dirtySections
export const selectAutoSaveStatus = (state) => state.receiptConfigure.ui.autoSaveStatus

/* ------------- Persistence ------------- */
// Entire slice is session-only: every mount re-fetches via loadProfile() and
// hydrateConfigure() replaces it. Persisting would restore another BA's answers
// before the authoritative GET resolves.

const PERSISTED_REDUCER_VERSION = 1

export const persistedReducer = persistReducer(
  {
    key: "receiptConfigureThunk",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    blacklist: ["profile", "ui"],
    migrate: async (state) => state
  },
  slice.reducer
)

export const reducer = persistedReducer
